import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin-api";
import type { ProductBrand } from "@/types";

/**
 * Fetch the brands for a network (sourceId) — the second stage of the network→brand cascade.
 * Refetches when the network changes; sourceId undefined/null returns an empty list without
 * fetching (no network selected yet). pageSize 200 is a documented v1 cap.
 */
export function useBrandOptions(sourceId: number | null | undefined): {
  brands: ProductBrand[];
  isLoading: boolean;
} {
  const [brands, setBrands] = useState<ProductBrand[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (sourceId == null) {
      setBrands([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    // Clear the previous network's options immediately — a stale list rendered while the new
    // network's brands load would let the user pick a brand from the wrong network.
    setBrands([]);
    setIsLoading(true);
    adminApi.brands
      .list({ sourceId, pageSize: 200, sortBy: "name" })
      .then((res) => {
        if (!cancelled) setBrands(res.items);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err instanceof Error ? err.message : "Failed to load brands.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  return { brands, isLoading };
}
